/**
 * MentionText — renders plain post/comment/message text with URLs, @mentions,
 * and #hashtags as clickable links. Mentions open the member's profile;
 * hashtags open the feed filtered by that tag.
 *
 * The API keeps mentions inside the body text, so each label is matched once,
 * here, against the people known for this context (`mentions`). The resolved
 * identity travels with the segment and drives navigation, so clicking never
 * depends on the label still matching a username.
 */
import { type MouseEvent, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Hashtag } from './Hashtag';
import { useMentionNavigate } from './richTextNavigation';
import type { MentionIndex, MentionMatch } from '@wlb/shared/utils/mentions';
import {
  getDeepLinkPrefixes,
  parseDeepLink,
} from '@wlb/shared/utils/deepLinking';
import { parseRichText } from '../../utils/parseRichText';
import './richText.css';

export { parseRichText } from '../../utils/parseRichText';

interface MentionTextProps {
  text: string;
  /** Identities known for this post/comment/thread, from `buildMentionIndex`. */
  mentions?: MentionIndex;
  /** Keeps the stored @ token while allowing Facebook-style display names. */
  showMentionAt?: boolean;
  /** Messages expose protected reshare sources as clean internal post links. */
  showReshareSourceUrls?: boolean;
}

export const formatMentionLabel = (
  label: string,
  showMentionAt = true,
): string => `${showMentionAt ? '@' : ''}${label}`;

export const shouldOpenRichTextUrlInNewTab = (
  href: string,
  currentOrigin = typeof window === 'undefined' ? '' : window.location.origin,
): boolean => {
  if (!currentOrigin) return true;
  try {
    return new URL(href, currentOrigin).origin !== new URL(currentOrigin).origin;
  } catch {
    return true;
  }
};

export const getInternalPostDestination = (
  href: string,
  currentOrigin = typeof window === 'undefined' ? '' : window.location.origin,
): string | null => {
  if (!currentOrigin) return null;

  try {
    const url = new URL(href, currentOrigin);
    const allowedOrigins = new Set([
      new URL(currentOrigin).origin,
      ...getDeepLinkPrefixes()
        .filter(prefix => /^https?:\/\//i.test(prefix))
        .map(prefix => new URL(prefix).origin),
    ]);
    if (!allowedOrigins.has(url.origin)) return null;

    const target = parseDeepLink(url.toString());
    return target?.type === 'post'
      ? `/?postId=${encodeURIComponent(target.postId)}`
      : null;
  } catch {
    return null;
  }
};

export function MentionText({
  text,
  mentions,
  showMentionAt = true,
  showReshareSourceUrls = false,
}: MentionTextProps) {
  const navigateToMention = useMentionNavigate();
  const navigate = useNavigate();

  const segments = useMemo(
    () => parseRichText(text, mentions, { showReshareSourceUrls }),
    [mentions, showReshareSourceUrls, text],
  );

  const handleMentionClick = (
    match: MentionMatch,
    event: MouseEvent<HTMLButtonElement>,
  ) => {
    event.stopPropagation();
    navigateToMention(match);
  };

  return (
    <>
      {segments.map((segment, index) => {
        if (segment.type === 'mention') {
          const { userGuid, ambiguous, displayName } = segment.match;
          // Only a uniquely resolved mention is clickable; ambiguous or
          // unknown labels stay as plain text so they never navigate wrongly.
          if (!userGuid || ambiguous) {
            return (
              <span key={index} className="mention-label">
                {formatMentionLabel(segment.value, showMentionAt)}
              </span>
            );
          }
          const label = displayName || segment.value;
          return (
            <button
              key={index}
              type="button"
              className="mention-link"
              onClick={event => handleMentionClick(segment.match, event)}
            >
              {formatMentionLabel(label, showMentionAt)}
            </button>
          );
        }
        if (segment.type === 'hashtag') {
          return <Hashtag key={index}>{segment.value}</Hashtag>;
        }
        if (segment.type === 'url') {
          const internalPostDestination = getInternalPostDestination(segment.href);
          const opensInNewTab = internalPostDestination
            ? false
            : shouldOpenRichTextUrlInNewTab(segment.href);
          return (
            <a
              key={index}
              className="text-link"
              href={segment.href}
              target={opensInNewTab ? '_blank' : undefined}
              rel={opensInNewTab ? 'noopener noreferrer' : undefined}
              onClick={event => {
                event.stopPropagation();
                if (internalPostDestination) {
                  event.preventDefault();
                  navigate(internalPostDestination);
                }
              }}
            >
              {segment.value}
            </a>
          );
        }
        return <span key={index}>{segment.value}</span>;
      })}
    </>
  );
}
